import { useEffect, type ReactNode } from 'react';
import { FOCUS_RING } from './tone';
import { Button } from './Controls';

// Phase 36 §21b — overlays.
//
// One elevation above the cards, and the only second shadow in the system. If
// a third elevation appears, the reader stops being able to tell what is
// modal, what is transient and what is simply a card.

const MONO = 'co-mono';

const POP: React.CSSProperties = {
  borderRadius: 8,
  boxShadow: 'var(--co-shadow-pop)',
};

export const POP_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

/**
 * A modal whose confirm button states the consequence and the count.
 *
 * `OK` makes the reader re-read the sentence above to work out what they are
 * agreeing to. `Move 14 articles` can be checked against the body in one
 * glance, which is the only moment the check is worth anything.
 */
export function Modal({
  title,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  destructive = false,
  busy = false,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
  busy?: boolean;
}) {
  // Escape closes. A dialog that traps the reader with no keyboard way out is
  // the one interaction people describe as "it froze".
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="fixed inset-0 bg-[rgba(35,32,28,.45)]"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative w-full max-w-md overflow-hidden ${POP_CLASS}`}
        style={POP}
      >
        <div className="border-b border-[var(--co-line-subtle)] px-[18px] py-4">
          <h2 className="text-[16px] font-bold leading-[1.15] tracking-[-.015em] text-[var(--co-ink)]">
            {title}
          </h2>
        </div>
        <div
          className="px-[18px] py-4 text-[12.5px] leading-[1.6] text-[var(--co-ink-2)]"
          style={{ textWrap: 'pretty' } as React.CSSProperties}
        >
          {children}
        </div>
        <div className="flex justify-end gap-[9px] border-t border-[var(--co-line)] bg-[var(--co-selected)] px-[18px] py-3">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface MenuItem {
  id: string;
  label: string;
  onClick: () => void;
  /** Drawn in burgundy and pushed below a rule. At most one per menu. */
  destructive?: boolean;
  disabled?: boolean;
}

/** Destructive item last, separated by a rule — never adjacent to a routine one. */
export function Menu({ items, label }: { items: readonly MenuItem[]; label: string }) {
  const ordered = [...items].sort(
    (a, b) => Number(Boolean(a.destructive)) - Number(Boolean(b.destructive)),
  );
  return (
    <div
      role="menu"
      aria-label={label}
      className={`w-[210px] p-[6px] ${POP_CLASS}`}
      style={POP}
    >
      {ordered.map((item, index) => {
        const first = item.destructive && !ordered[index - 1]?.destructive && index > 0;
        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={item.onClick}
            className={
              'block w-full rounded-md px-[10px] py-2 text-left text-[12.5px] transition-colors ' +
              'hover:bg-[var(--co-selected)] disabled:cursor-not-allowed disabled:opacity-50 ' +
              (first ? 'mt-1 border-t border-[var(--co-line-subtle)] pt-[10px] ' : '') +
              FOCUS_RING
            }
            style={{ color: item.destructive ? 'var(--co-burgundy)' : 'var(--co-ink-2)' }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Ink, so it never competes with a status colour.
 *
 * A green toast beside a green chip makes the chip look like part of the
 * notification. It carries the undo rather than a dismiss ✕ — the reader who
 * wants it gone will ignore it, and the one who made a mistake needs the verb.
 */
export function Toast({
  message,
  onUndo,
  tone = 'ok',
}: {
  message: string;
  onUndo?: () => void;
  tone?: 'ok' | 'danger';
}) {
  return (
    <div
      role="status"
      className="flex w-[300px] items-start gap-[11px] rounded-lg px-[15px] py-[13px]"
      style={{ background: 'var(--co-ink)', boxShadow: 'var(--co-shadow-pop)' }}
    >
      <span
        className="mt-[1px] flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] text-[11px] font-bold"
        style={{
          background: tone === 'ok' ? 'var(--co-brass-pale)' : 'var(--co-burgundy-pale)',
          color: 'var(--co-ink)',
        }}
        aria-hidden="true"
      >
        {tone === 'ok' ? '✓' : '!'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] leading-[1.5] text-[var(--co-on-ink)]">{message}</p>
        {onUndo && (
          <button
            type="button"
            onClick={onUndo}
            className={`${MONO} mt-[7px] text-[9.5px] font-semibold uppercase tracking-[.11em] text-[var(--co-brass-pale)] hover:underline ${FOCUS_RING}`}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Explains an EFFECT.
 *
 * If a control needs a tooltip to be usable at all, the fix is to relabel the
 * control: a tooltip is invisible on touch, to keyboard users who tab past it,
 * and to anyone reading quickly.
 */
export function Tooltip({ children }: { children: ReactNode }) {
  return (
    <span
      role="tooltip"
      className="inline-block max-w-[210px] rounded-md px-[10px] py-[7px] text-[11.5px] leading-[1.45]"
      style={{ background: 'var(--co-ink)', color: 'var(--co-on-ink)' }}
    >
      {children}
    </span>
  );
}
